import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { buildLegalLd } from "@/lib/page-schemas";

const TITLE = "Privacy Policy — DrHint Data Ownership Transparency";
const DESC =
  "How DrHint handles cookies, navigation analytics, Google AdSense, and ads.txt ownership. Full GDPR / CCPA transparency for users in EU, UK, US and global jurisdictions.";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "index,follow,max-snippet:-1" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://drhint.com/privacy-policy" },
    ],
    links: [{ rel: "canonical", href: "https://drhint.com/privacy-policy" }],
    scripts: buildLegalLd({
      url: "https://drhint.com/privacy-policy",
      name: TITLE,
      description: DESC,
      lastModified: "2026-06-01",
      trail: [
        { name: "Home", item: "https://drhint.com/" },
        { name: "Privacy Policy", item: "https://drhint.com/privacy-policy" },
      ],
    }),
  }),
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  return (
    <Layout>
      <article className="mx-auto max-w-3xl px-4 py-10 text-sm leading-relaxed">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="mt-2 text-xs text-muted-foreground">Data Ownership &amp; Transparency · Last updated: 16 June 2026</p>

        <p className="mt-6 text-muted-foreground">
          DrHint ("we", "us", "our") operates drhint.com as a free, multi-utility
          informational platform. This Privacy Policy explains, in plain language,
          how we collect, process and protect any data your browser exchanges with
          our servers and our advertising partners.
        </p>

        <h2 className="mt-8 text-xl font-bold">1. Browser confidentiality &amp; cookies</h2>
        <p className="mt-2 text-muted-foreground">
          Cookies, local-storage entries, site-directory navigation events and
          structural form inputs (e.g. bill reference numbers, calculator
          slider values) are treated with high browser confidentiality. They
          are processed in your browser to render results and are never sold,
          rented or shared as identifiable personal data. We do not require an
          account and we do not link visits to a real-world identity.
        </p>

        <h2 className="mt-8 text-xl font-bold">2. Google AdSense &amp; programmatic advertising</h2>
        <p className="mt-2 text-muted-foreground">
          DrHint participates in the Google AdSense architecture framework. Google
          and its partner ad networks may set or read cookies on your device to
          serve contextually relevant ads. To guarantee full transparency for
          search crawlers and ad-verification bots, our publisher ownership is
          declared in our verified{" "}
          <a href="/ads.txt" className="text-primary underline">/ads.txt</a> file
          at the site root, listing every authorized seller of advertising
          inventory across DrHint.
        </p>
        <p className="mt-2 text-muted-foreground">
          You may opt out of personalised advertising at{" "}
          <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google Ads Settings</a>{" "}
          or{" "}
          <a href="https://www.aboutads.info/" target="_blank" rel="noopener noreferrer" className="text-primary underline">aboutads.info</a>.
        </p>

        <h2 className="mt-8 text-xl font-bold">3. Server logs &amp; analytics</h2>
        <p className="mt-2 text-muted-foreground">
          Standard web logs may record your IP address, user-agent, referrer
          and timestamp for security, fraud detection and aggregate analytics.
          We do not attempt to re-identify visitors from this data.
        </p>

        <h2 className="mt-8 text-xl font-bold">4. GDPR (EU / UK) rights</h2>
        <p className="mt-2 text-muted-foreground">
          If you are in the EEA or UK you have the right to access, rectify,
          erase, restrict, or port your personal data and to withdraw consent
          at any time. Email{" "}
          <a href="mailto:attapops4@gmail.com" className="text-primary underline">attapops4@gmail.com</a>.
        </p>

        <h2 className="mt-8 text-xl font-bold">5. CCPA / CPRA (California) rights</h2>
        <p className="mt-2 text-muted-foreground">
          California residents may request access, deletion, correction, and may
          opt-out of any "sale" or "sharing" of personal information. DrHint does
          not sell personal information for money; advertising cookies set by
          partners may, under California law, constitute "sharing", which you
          may decline via the Global Privacy Control (GPC) signal.
        </p>

        <h2 className="mt-8 text-xl font-bold">6. Children</h2>
        <p className="mt-2 text-muted-foreground">
          DrHint is not directed at children under 13 and we do not knowingly
          collect data from them.
        </p>

        <h2 className="mt-8 text-xl font-bold">7. Updates &amp; contact</h2>
        <p className="mt-2 text-muted-foreground">
          Material changes will be posted on this page with a revised "last
          updated" date. For any privacy question see{" "}
          <Link to="/contact" className="text-primary underline">/contact</Link>{" "}
          or our{" "}
          <Link to="/disclaimer" className="text-primary underline">Disclaimer</Link>.
        </p>
      </article>
    </Layout>
  );
}